import type { AppLocale } from "./types";

/** Stable localStorage key for the user's language preference (browser-only). */
export const LOCALE_STORAGE_KEY = "agentJudge.locale";

export const SUPPORTED_LOCALES: readonly AppLocale[] = ["zh-CN", "en-US", "de-DE"];

export const DEFAULT_LOCALE: AppLocale = "zh-CN";

export function isAppLocale(value: unknown): value is AppLocale {
  return value === "zh-CN" || value === "en-US" || value === "de-DE";
}

/** Map a browser language tag onto a supported app locale. */
export function matchBrowserLocale(language: string | undefined | null): AppLocale | null {
  const raw = String(language ?? "").trim().toLowerCase();
  if (!raw) return null;
  if (raw === "zh-cn" || raw === "zh" || raw.startsWith("zh-hans") || raw.startsWith("zh-cn")) return "zh-CN";
  if (raw.startsWith("zh")) return "zh-CN";
  if (raw === "en" || raw.startsWith("en-")) return "en-US";
  if (raw === "de" || raw.startsWith("de-")) return "de-DE";
  return null;
}

/**
 * Resolve the active locale:
 * saved localStorage choice > browser language > zh-CN.
 */
export function resolveLocale(options?: {
  stored?: string | null;
  browserLanguages?: readonly string[];
}): AppLocale {
  const stored = options?.stored;
  if (isAppLocale(stored)) return stored;

  const languages = options?.browserLanguages ?? (
    typeof navigator !== "undefined"
      ? (navigator.languages?.length ? navigator.languages : [navigator.language])
      : []
  );
  for (const language of languages) {
    const matched = matchBrowserLocale(language);
    if (matched) return matched;
  }
  return DEFAULT_LOCALE;
}

export function readStoredLocale(storage: Pick<Storage, "getItem"> | null | undefined = typeof localStorage !== "undefined" ? localStorage : null): string | null {
  try {
    return storage?.getItem(LOCALE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeStoredLocale(
  locale: AppLocale,
  storage: Pick<Storage, "setItem"> | null | undefined = typeof localStorage !== "undefined" ? localStorage : null,
): void {
  try {
    storage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore quota / private-mode failures; in-memory locale still applies.
  }
}

export function applyDocumentLocale(locale: AppLocale, doc: Document = document): void {
  doc.documentElement.lang = locale;
}
